/**
 * The partner's session.
 *
 * Partners are their own accounts (not job seekers, not admins): one access
 * token from `POST /partner/auth/login`, valid 12 h, no refresh. When it runs
 * out, or the server says the account was suspended, the session is cleared
 * and the sign-in page says why.
 */
import { create } from 'zustand'
import { persist } from 'zustand/middleware'

import { ApiError, configureClient } from '@/api/client'
import { authApi, partnerApi } from '@/api/endpoints'
import type { Partner } from '@/types/api'

export type SignedOutReason = 'expired' | 'suspended' | null

interface AuthState {
  partner: Partner | null
  accessToken: string | null
  /** Epoch ms. */
  expiresAt: number | null
  status: 'idle' | 'authenticating' | 'authenticated' | 'anonymous'
  reason: SignedOutReason
  login: (email: string, password: string) => Promise<void>
  logout: () => void
  hydrate: () => Promise<void>
  clear: (reason?: SignedOutReason) => void
  setPartner: (partner: Partner) => void
}

const TWELVE_HOURS = 12 * 60 * 60

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      partner: null,
      accessToken: null,
      expiresAt: null,
      status: 'idle',
      reason: null,

      clear: (reason = null) => set({ partner: null, accessToken: null, expiresAt: null, status: 'anonymous', reason }),

      setPartner: (partner) => set({ partner }),

      login: async (email, password) => {
        set({ status: 'authenticating', reason: null })
        try {
          const session = await authApi.login({ email, password })
          const seconds = session.expiresIn > 0 ? session.expiresIn : TWELVE_HOURS
          set({
            partner: session.partner,
            accessToken: session.accessToken,
            expiresAt: Date.now() + seconds * 1000,
            status: 'authenticated',
          })
        } catch (error) {
          set({ partner: null, accessToken: null, expiresAt: null, status: 'anonymous' })
          throw error
        }
      },

      logout: () => get().clear(),

      hydrate: async () => {
        const { accessToken, expiresAt } = get()
        if (!accessToken) {
          set({ status: 'anonymous' })
          return
        }
        if (expiresAt && expiresAt <= Date.now()) {
          get().clear('expired')
          return
        }
        try {
          set({ partner: await partnerApi.me(), status: 'authenticated' })
        } catch (error) {
          // Offline or a server hiccup: keep the session and let the pages retry.
          if (error instanceof ApiError && error.status !== 401 && error.status !== 403) {
            set({ status: get().partner ? 'authenticated' : 'anonymous' })
            return
          }
          if (get().status !== 'anonymous') get().clear(error instanceof ApiError && error.code === 'partner_suspended' ? 'suspended' : 'expired')
        }
      },
    }),
    {
      name: 'jobslide-partner-auth',
      partialize: (state) => ({ partner: state.partner, accessToken: state.accessToken, expiresAt: state.expiresAt }),
    },
  ),
)

/** Wire the HTTP client to this store. Called once, at startup. */
export function initAuthBridge(): void {
  configureClient({
    getAccessToken: () => {
      const { accessToken, expiresAt } = useAuthStore.getState()
      if (accessToken && expiresAt && expiresAt <= Date.now()) {
        useAuthStore.getState().clear('expired')
        return null
      }
      return accessToken
    },
    onUnauthorized: (error) => {
      if (useAuthStore.getState().status === 'anonymous') return
      useAuthStore.getState().clear(error.code === 'partner_suspended' ? 'suspended' : 'expired')
    },
  })

  // Sign out on the dot when the 12 h are up, even on an idle tab.
  window.setInterval(() => {
    const { accessToken, expiresAt, clear } = useAuthStore.getState()
    if (accessToken && expiresAt && expiresAt <= Date.now()) clear('expired')
  }, 60_000)
}
